<template>
  <div class="array-panel">
    <!-- 方阵标题栏 -->
    <div class="array-header" @click="toggleExpand">
      <div class="array-title">{{ arrayData.sort }}#储能方阵</div>
      <div class="array-time-row">
        <div class="data-update-arrow" :class="{ 'data-update-arrow--active': flowPulseActive }" aria-hidden="true">
          <span class="data-update-arrow__glyph">↓</span>
        </div>
        <div class="array-time">采样时间：{{ formatTime(realData.record_time) ?? '--' }}</div>
      </div>
      <img src="@/assets/images/station-monitor/detail-dialog/collapse-icon.png" class="collapse-icon"
        :class="{ 'expanded': isExpanded }" alt="展开/收起" />
    </div>

    <!-- 方阵内容（使用CSS过渡） -->
    <transition name="panel-expand">
      <div v-show="isExpanded" class="array-content-wrapper">
        <div class="array-content">
          <!-- eLink 信息卡片（左侧） -->
          <div class="elink-info-card" :data-device-id="arrayData.uuid">
            <!-- eLink标题和详情按钮 -->
            <div class="elink-header">
              <div class="elink-name">
                {{ arrayData.eLinkName }}
                <div class="elink-uuid">UUID: {{ arrayData.uuid }}</div>
              </div>
              <v-btn variant="text" color="primary" @click.stop="$emit('open-elink-detail', arrayData)">详情</v-btn>
            </div>

            <!-- eLink中间图表区域 -->
            <div class="elink-chart-area">
              <div class="elink-device-image">
                <img src="@/assets/images/station-monitor/detail-dialog/elink-device.png" alt="eLink设备"
                  loading="lazy" />
              </div>
              <div class="device-name">{{ arrayData.productModel }}</div>
            </div>

            <!-- eLink底部信息区域 -->
            <div class="elink-info-list">
              <div class="info-headers">
                <div class="info-label">工作状态</div>
                <div class="info-label">空调状态</div>
                <div class="info-label">消防状态</div>
              </div>

              <div class="info-values-row">

                <div class="info-status-column">
                  <div class="status-indicator" :style="{ backgroundColor: EblockStatusMap.getColor(realData.run_st) }"></div>
                  <div class="status-text" :style="{ color: EblockStatusMap.getColor(realData.run_st) }">{{ EblockStatusMap.getText(realData.run_st) }}
                  </div>
                </div>

                <div class="info-status-column">
                  <div class="status-indicator" :style="{ backgroundColor: AirStatusMap.getColor(realData.airc_st) }"></div>
                  <div class="status-text" :style="{ color: AirStatusMap.getColor(realData.airc_st) }">{{
                    AirStatusMap.getText(realData.airc_st, '--') }}
                  </div>
                </div>

                <div class="info-status-column">
                  <div class="status-indicator"
                    :style="{ backgroundColor: FireStatusMap.getColor(realData.alarm_level) }"></div>
                  <div class="status-text" :style="{ color: FireStatusMap.getColor(realData.alarm_level) }">
                    {{ FireStatusMap.getText(realData.alarm_level, '--') }}
                  </div>
                </div>


              </div>
            </div>

            <div class="elink-info-list" v-if="realData.airc_st != null">
              <div class="info-headers">

                <div class="info-label">实时功率</div>
                <div class="info-label">soc</div>
                <!-- 占位 -->
                <div class="info-label"></div>
              </div>

              <div class="info-values-row">
                <div class="info-power-column">                
                    {{ formatValue(realData.p_total) }}
                </div>

                <div class="info-soc-column">
                  <GradientCircle :size="36" :strokeWidth="4" :gradientColors="['#DEF8B3', '#8DD3F5']"
                    :progress="Number(realData.soc)" :showText="true" />
                </div>
                <!-- 占位 -->
                <div class="info-status-column"></div>
              </div>
            </div>
          </div>


          <!-- v-memo="[block.uuid, block.status, block.power, block.soc, block.socPercent, block.alarmCount, block.onlineText, block.raw?.airc_st, block.raw?.alarm_level, highlightedDevice.type === 'eblock' && highlightedDevice.id === block.uuid]" -->
          <!-- 能量块网格（右侧） -->
          <div class="energy-blocks-grid">
            <div v-for="block in arrayData.eBlocks" :key="block.uuid" class="block-card" :data-device-id="block.uuid">
              <!-- 顶部标题和状态栏 -->
              <div class="block-status-bar">
                <div class="block-title">
                  <div>{{ block.eBlockName }}</div>
                  <div class="block-uuid" @click.stop>UUID: {{ block.uuid }}</div>
                </div>
                <div class="status-badges">
                  <v-badge location="top right" color="#FF7867" :content="getRealData(block.uuid, 'alarmNum')" v-if="getRealData(block.uuid, 'alarmNum') > 0">
                    <div class="warning-badge">告警</div>
                  </v-badge>
                  <div class="online-badge" :class="getEBlockOnlineClass(getRealData(block.uuid, 'state_eblock'))">{{ getEBlockOnlineText(getRealData(block.uuid, 'state_eblock'))  }}</div>
                  <v-btn variant="text" color="primary" @click="openEblockDetail(block, arrayData.id)">详情</v-btn>
                </div>
              </div>

              <!-- 主内容区域 -->
              <div class="block-content-wrapper">
                <!-- 左侧能量块图片 -->
                <div class="block-image">
                  <div class="image-container">
                    <img src="@/assets/images/station-monitor/detail-dialog/block-bottom.png" class="block-bottom-image"
                      alt="block底部" loading="lazy" />
                    <img :src="getBlockImage(block.productModel)" class="block-main-image" alt="block主体" loading="lazy" />
                  </div>
                  <div class="model-name">{{ block.productModel }}</div>
                </div>

                <!-- 右侧信息区域 -->
                <div class="block-info">
                  <div class="info-row">
                    <div class="label">工作状态</div>
                    <div class="value-wrapper">
                      <div class="status-indicator" :style="{ backgroundColor: EblockStatusMap.getColor(getRealData(block.uuid, 'state_eblock')) }"></div>
                      <div class="value status-value" :style="{ color: EblockStatusMap.getColor(getRealData(block.uuid, 'state_eblock')) }">{{ EblockStatusMap.getText(getRealData(block.uuid, 'state_eblock'), '--') }}</div>
                    </div>
                  </div>
                  <div class="info-row">
                    <div class="label">空调状态</div>
                    <div class="value-wrapper">
                      <div class="status-indicator" :style="{ backgroundColor: AirStatusMap.getColor(getRealData(block.uuid, 'airc_st')) }">
                      </div>
                      <div class="value status-value" :style="{ color: AirStatusMap.getColor(getRealData(block.uuid, 'airc_st')) }">{{
                        AirStatusMap.getText(getRealData(block.uuid, 'airc_st'), '--') }}</div>
                    </div>
                  </div>
                  <div class="info-row">
                    <div class="label">消防状态</div>
                    <div class="value-wrapper">
                      <div class="status-indicator"
                        :style="{ backgroundColor: FireStatusMap.getColor(getRealData(block.uuid, 'alarm_level')) }"></div>
                      <div class="value status-value" :style="{ color: FireStatusMap.getColor(getRealData(block.uuid, 'alarm_level')) }">
                        {{ FireStatusMap.getText(getRealData(block.uuid, 'alarm_level'), '--') }}</div>
                    </div>
                  </div>


                  <v-divider class="my-2" color="rgba(239, 244, 251, 0.8)" opacity="1"></v-divider>
                  <div class="info-row">
                    <div class="label">实时功率</div>
                    <div class="value-wrapper">
                      <div class="value">
                       {{  formatValue(getRealData(block.uuid, 'p_ac')) }}
                      </div>
                      <div class="unit">kW</div>
                    </div>
                  </div>

                  <div class="info-row">
                    <div class="label">soc</div>
                    <div class="value-wrapper">
                      <div class="soc-progress-bar">
                        <div class="soc-fill" :style="{ width: getRealData(block.uuid, 'soc')+'%' }"></div>
                      </div>
                      <div class="value">{{ formatValue(getRealData(block.uuid, 'soc'))}}</div>
                      <div class="unit">%</div>
                    </div>
                  </div>

                  <!-- 底部按钮组 -->
                  <div class="block-actions">
                    <!-- <div class="action-btn" @click.stop="$emit('open-eblock-detail', block)">详情</div>
                    <div class="action-btn" @click.stop="$emit('open-pcs-detail', block)">pcs</div>
                    <div class="action-btn" @click.stop="$emit('open-cluster-detail', block)">电池簇</div> -->
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, watch, onBeforeUnmount, inject, computed } from 'vue'
import GradientCircle from '@/components/GradientCircle/index.vue'
import { createStatusMap } from '@/utils'
import { useRouter, useRoute } from 'vue-router'
import dayjs from 'dayjs'
import { dataMapKey } from '../../tabs/EnergyStorageSystem/keys'
import { EBlockWorkStatus, formatStatus, EblockStatusMap, getEBlockOnlineText, getEBlockOnlineClass } from './monitorEnums'

// 导入所有 block-main 图片
import blockMain172 from '@/assets/images/station-monitor/detail-dialog/block-main-172.png'
import blockMain200 from '@/assets/images/station-monitor/detail-dialog/block-main-200.png'
import blockMain230 from '@/assets/images/station-monitor/detail-dialog/block-main-230.png'
import blockMain250 from '@/assets/images/station-monitor/detail-dialog/block-main-250.png'
import blockMain261 from '@/assets/images/station-monitor/detail-dialog/block-main-261.png'
import blockMain372 from '@/assets/images/station-monitor/detail-dialog/block-main-372.png'
import blockMain836 from '@/assets/images/station-monitor/detail-dialog/block-main-836.png'


const router = useRouter()
const route = useRoute()


// 0:停机 1:制热 2:制冷 3:故障
const AirStatusMap = createStatusMap({
  0: { text: '停机', color: 'var(--color-stopped)' },
  1: { text: '制热', color: 'red' },
  2: { text: '制冷', color: 'blue' },
  3: { text: '故障', color: 'red' },
  4: { text: '除湿', color: 'var(--color-success)' },
})
// 0正常 1一级预警 2二级预警 3三级预警
const FireStatusMap = createStatusMap({
  0: { text: '正常', color: 'var(--color-success)' },
  1: { text: '一级预警', color: 'orange' },
  2: { text: '二级预警', color: 'red' },
  3: { text: '三级预警', color: 'red' },
})



// block 图片映射表
const blockImageMap = {
  '172': blockMain172,
  '200': blockMain200,
  '230': blockMain230,
  '250': blockMain250,
  '261': blockMain261,
  '372': blockMain372,
  '836': blockMain836
}

// 支持的 model 数字列表
const supportedModels = ['172', '200', '230', '250', '261', '372', '836']

// 根据 model 获取对应的 block 图片
const getBlockImage = (model) => {
  if (!model) return blockMain200
  // 从 model 字符串中提取数字
  const match = String(model).match(/\d+/)
  if (match) {
    const modelNumber = match[0]
    if (supportedModels.includes(modelNumber)) {
      return blockImageMap[modelNumber]
    }
  }
  // 默认返回 200
  return blockMain200
}

// 格式化数值，区分 0 和 无数据
const formatValue = (val) => {
  if (val === null || val === undefined || val === '') return '--'
  return val
}

const formatTime = (time) => {
  if (!time) return '--'
  return dayjs(time).format('YYYY-MM-DD HH:mm:ss')
}

const props = defineProps({
  arrayData: {
    type: Object,
    required: true
  },
  defaultExpanded: {
    type: Boolean,
    default: true
  },
  getELinkIdByEBlockId: {
    type: Function,
    default: () => { }
  }
})

const dataMap = inject(dataMapKey)
const realData = computed(() => {
  return getRealData(props.arrayData.uuid)
})

function getRealData(uuid, key) {
  const obj = dataMap.value[uuid] || {}
  if (!key) {
    return obj
  }
  return obj[key]
}

defineEmits(['open-elink-detail', 'open-eblock-detail', 'open-pcs-detail', 'open-cluster-detail'])

const isExpanded = ref(props.defaultExpanded)

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value
}

// 暴露展开方法给父组件调用
const expand = () => {
  isExpanded.value = true
}

defineExpose({
  expand
})

/** 数据更新时在「采样时间」前短暂显示简单箭头 */
const flowPulseActive = ref(false)
let flowPulseHideTimer = null
const FLOW_PULSE_MS = 520


watch(
  () => realData.value,
  (_next, prev) => {
    if (prev === undefined) return
    if (flowPulseHideTimer != null) {
      clearTimeout(flowPulseHideTimer)
      flowPulseHideTimer = null
    }
    flowPulseActive.value = false
    requestAnimationFrame(() => {
      flowPulseActive.value = true
      flowPulseHideTimer = window.setTimeout(() => {
        flowPulseActive.value = false
        flowPulseHideTimer = null
      }, FLOW_PULSE_MS)
    })
  }
)

onBeforeUnmount(() => {
  if (flowPulseHideTimer != null) {
    clearTimeout(flowPulseHideTimer)
    flowPulseHideTimer = null
  }
})



function openEblockDetail(block, elinkId) {
  router.push({
    // path: '/station-monitor/detail',
    query: {
      stationId: route.query.stationId,
      elinkId: elinkId,
      deviceId: block.uuid,
      deviceType: 'eBlock',
    }
  })
}


</script>

<style scoped lang="scss">
.array-panel {
  background: rgba(255, 255, 255, 1);
  border-radius: 20px;
  overflow: visible;
}

.array-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 22px;
  cursor: pointer;
  user-select: none;
  border-radius: 20px 20px 0 0;

  &:hover {
    background-color: rgba(246, 251, 255, 0.5);
  }
}

.array-title {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 16px;
  color: rgba(46, 46, 46, 1);
  font-weight: normal;
}

.array-time-row {
  position: relative;
  display: flex;
  align-items: center;
  margin-left: auto;
  margin-right: 16px;
  min-width: 0;
}

.array-time {
  color: #0009 !important;
  font-size: 14px;
  white-space: nowrap;
}

.collapse-icon {
  width: 11px;
  height: 6px;
  transition: transform 0.3s ease;

  &.expanded {
    transform: rotate(180deg);
  }
}

/* 过渡动画 */
.panel-expand-enter-active,
.panel-expand-leave-active {
  transition: max-height 0.3s ease, opacity 0.3s ease;
  overflow: hidden;
}

.panel-expand-enter-from,
.panel-expand-leave-to {
  max-height: 0;
  opacity: 0;
}

.panel-expand-enter-to,
.panel-expand-leave-from {
  max-height: 2000px;
  opacity: 1;
}

.array-content-wrapper {
  padding: 13px 22px 20px;
}

.array-content {
  display: flex;
  gap: 18px;
}

/* 数据更新：采样时间左侧简单箭头，无渐变与流线动画 */
.data-update-arrow {
  position: absolute;
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-right: 4px;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;

  &__glyph {
    display: block;
    font-size: 14px;
    line-height: 1;
    color: #3a68ff;
  }

  &--active {
    visibility: visible;
    animation: dataUpdateArrowShow 0.48s ease-out forwards;
  }
}

@keyframes dataUpdateArrowShow {
  0% {
    opacity: 0;
  }

  22% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}

/* eLink 信息卡片 */
.elink-info-card {
  min-width: 275px;
  height: 100%;
  width: 275px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
  border: 1px solid #F4F7FC;
  border-radius: 10px;
  padding: 0px;
  box-sizing: border-box;
  transition: border-color 0.3s ease;
  /* 移除 contain 以避免圆环被裁剪 */
  will-change: border-color, box-shadow;
  overflow: visible;

  &.highlight-device {
    animation: highlightBlink 0.5s ease-in-out 6;
  }
}

@keyframes highlightBlink {

  0%,
  100% {
    border-color: rgba(244, 247, 252, 1);
    box-shadow: none;
  }

  50% {
    border-color: rgba(58, 104, 255, 1);
    box-shadow: 0 0 12px rgba(58, 104, 255, 0.5);
  }
}

.elink-header {
  width: 100%;
  height: 48px;
  background: rgba(239, 244, 251, 0.7);
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
  box-sizing: border-box;
}

.elink-name {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 14px;
  color: rgba(46, 46, 46, 1);
  font-weight: normal;
}

.detail-link {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 14px;
  color: rgba(58, 104, 255, 1);
  cursor: pointer;
}

.elink-chart-area {
  width: 100%;
  height: 261px;
  background: linear-gradient(0deg, rgba(239, 244, 251, 0.7) 0%, rgba(255, 255, 255, 0.7) 100%);
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  overflow: hidden;
  position: relative;
}

.elink-device-image {
  display: flex;
  flex-direction: column;
  align-items: center;

  img {
    width: 71px;
    height: 179px;
  }
}

.device-name {
  margin-top: 7px;
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 12px;
  color: rgba(46, 46, 46, 1);
}

.elink-info-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, rgba(239, 244, 251, 0.7) 0%, rgba(255, 255, 255, 0.7) 100%);
  border-radius: 5px;
  padding: 20px 30px 10px 30px;
  box-sizing: border-box;
  overflow: visible;
}

.info-headers {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
}

.info-label {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 12px;
  color: rgba(46, 46, 46, 1);
  font-weight: normal;
  white-space: nowrap;
  flex: 1;
  text-align: center;

  &:first-child {
    text-align: left;
  }
}

.info-values-row {
  display: flex;
  align-items: center;
  gap: 0;
  min-height: 50px;
  overflow: visible;
}

.info-status-column {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;

  .status-indicator {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .status-text {
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 14px;
    font-weight: normal;
  }
}

.info-power-column {
  flex: 1;
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 14px;
  color: rgba(46, 46, 46, 1);
  font-weight: normal;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
}

.info-soc-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  overflow: visible;
}

/* 能量块网格 */
.energy-blocks-grid {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(356px, 1fr));
  gap: 12px;
  align-content: start;
}

/* 能量块卡片 */
.block-card {
  position: relative;
  min-width: 356px;
  width: 100%;
  min-height: 207px;
  // background: rgba(239, 244, 251, 0.7);
  border-radius: 8px;
  border: 1px solid #F4F7FC;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  transition: border-color 0.3s ease;
  /* 移除 contain 以避免内容被裁剪 */
  will-change: border-color, box-shadow;

  &.highlight-device {
    animation: highlightBlink 0.5s ease-in-out 6;
  }
}

.block-status-bar {
  position: relative;
  width: 100%;
  height: 48px;
  background: rgba(239, 244, 251, 0.7);
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 15px;
  box-sizing: border-box;
  border-radius: 6px;
}

.block-title {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 16px;
  color: rgba(46, 46, 46, 1);
  font-weight: normal;
}

.elink-uuid,
.block-uuid {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 10px;
  color: rgba(46, 46, 46, 0.6);
  font-weight: normal;
  cursor: default;
}

.status-badges {
  display: flex;
  align-items: center;
  gap: 22px;
  margin-left: auto;
}

.warning-badge {
  width: 50px;
  height: 20px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 10px;
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 12px;
  color: rgba(46, 46, 46, 1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.online-badge {
  width: 50px;
  height: 20px;
  border-radius: 10px;
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  &.online {
    background: rgba(184, 229, 191, 0.28);
    color: rgba(54, 182, 12, 1);
  }

  &.offline {
    background: rgba(229, 232, 241, 1);
    color: rgba(46, 46, 46, 1);
  }
}

.block-content-wrapper {
  display: flex;
  padding: 18px 15px 0;
  flex: 1;
  background: rgba(255, 255, 255, 1);
  border-radius: 6px;
}

.block-image {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 75px;
  flex-shrink: 0;
  margin-top: 2px;

  .image-container {
    position: relative;
    width: 75px;
    height: 94px;

    .block-bottom-image {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 75px;
      height: 18px;
    }

    .block-main-image {
      position: absolute;
      top: 0;
      left: 7px;
      width: 50px;
      height: 90px;
    }
  }

  .model-name {
    margin-top: 5px;
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 10px;
    color: rgba(46, 46, 46, 0.6);
    line-height: normal;
  }
}

.block-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
  margin-left: 50px;
}

.info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-right: 20px;

  .label {
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 14px;
    color: rgba(46, 46, 46, 1);
    line-height: normal;
  }

  .value-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .value {
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 14px;
    color: rgba(46, 46, 46, 1);
    line-height: normal;
  }
  .unit {
    color: rgba(94, 94, 94, 0.6);
    font-size: 12px;
  }

  .status-value {
    font-size: 14px;
  }

  .status-indicator {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }

  .power-icon-wrapper {
    position: relative;
    width: 38px;
    height: 13px;
    margin-right: 4px;

    img {
      position: absolute;
      width: 100%;
      height: auto;
    }

    .power-icon-base {
      top: 1px;
      left: 1px;
      width: 37px;
      height: 12px;
    }

    .power-icon-overlay {
      top: 0;
      left: 0;
      width: 38px;
      height: 10px;
    }
  }

  .soc-progress-bar {
    width: 76px;
    height: 8px;
    background: rgba(239, 244, 251, 1);
    border-radius: 20px;
    overflow: hidden;
    margin-right: 4px;

    .soc-fill {
      width: 0;
      height: 100%;
      background: linear-gradient(270deg, rgba(141, 211, 245, 1) 5.77%, rgba(184, 229, 214, 1) 52.89%, rgba(222, 248, 179, 1) 100%);
      border-radius: 20px;
      transition: width 0.3s ease;
    }
  }
}

.block-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  padding: 0 15px 20px;
  margin-top: auto;
}

.action-btn {
  width: 51px;
  height: 20px;
  background: rgba(58, 104, 255, 0.1);
  border-radius: 20px;
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 12px;
  list-style: none;
  color: rgba(58, 104, 255, 1);
  line-height: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(58, 104, 255, 0.2);
  }
}
</style>
